import React, { createContext, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useSearchParams } from 'react-router-dom';
import { TIMEZONE_STORAGE_KEY, getStoredTimezone, resolveTimezoneMode } from '../../utils/timezoneStorage';

dayjs.extend(utc);
dayjs.extend(timezone);

export const TimezoneContext = createContext();

export const TimezoneProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [persistedTimezone, setPersistedTimezone] = useState(() => getStoredTimezone());
  const activeTimezone = useMemo(
    () => resolveTimezoneMode(searchParams, persistedTimezone),
    [searchParams, persistedTimezone]
  );

  const getUserTimezoneAbbreviation = useCallback((datetime = new Date()) => {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
    const parts = formatter.formatToParts(datetime);
    const timeZoneAbbreviation = parts.find(part => part.type === 'timeZoneName')?.value || 'UTC';
    return timeZoneAbbreviation.match(/DT$|ST$/) ? timeZoneAbbreviation[0] + 'T' : timeZoneAbbreviation;
  }, []);

  const userTimezone = useMemo(() => getUserTimezoneAbbreviation(), [getUserTimezoneAbbreviation]);

  const convertToUtcIsoString = useCallback((localDate) => {
    const date = dayjs(localDate).toDate();
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      )
    ).toISOString();
  }, []);

  const createIsoStringWithTimezone = useCallback(
    (date) => {
      if (!date) return null;
      return activeTimezone === 'utc' ? convertToUtcIsoString(date) : dayjs(date).toISOString();
    },
    [activeTimezone, convertToUtcIsoString]
  );

  const formatDateTime = useCallback(
    (dateTime, format = 'YYYY-MM-DD HH:mm:ss.SSS') => {
      if (!dateTime) return '';
      const date = dayjs(dateTime);
      return activeTimezone === 'utc' ? date.utc().format(format) : date.format(format);
    },
    [activeTimezone]
  );

  const handleTimezoneChange = useCallback((newTimezone) => {
    const normalizedTimezone = newTimezone === 'utc' ? 'utc' : 'local';
    const nextParams = new URLSearchParams(searchParams);

    setPersistedTimezone(normalizedTimezone);
    window.localStorage.setItem(TIMEZONE_STORAGE_KEY, normalizedTimezone);

    if (normalizedTimezone === 'utc') {
      nextParams.set('timezone', 'utc');
    } else {
      nextParams.delete('timezone');
    }

    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const value = {
    timezone: activeTimezone,
    userTimezone,
    formatDateTime,
    createIsoStringWithTimezone,
    handleTimezoneChange,
  };

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};